import { useState, useCallback } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';

const BESTO_ADMIN_URL = process.env.REACT_APP_BESTO_ADMIN_URL || "/api";

export const useAdminApi = () => {
  const { token, logout } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const apiCall = useCallback(async (endpoint, method = 'GET', data = null) => {
    setLoading(true);
    setError(null);

    try {
      const config = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
        config.body = JSON.stringify(data);
      }

      const response = await fetch(`${BESTO_ADMIN_URL}${endpoint}`, config);
      const result = await response.json();

      if (response.status === 401) {
        // Token expired or invalid
        logout();
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        throw new Error(result.error || result.message || 'API call failed');
      }

      return result;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  // Convenience methods
  const get = useCallback((endpoint) => apiCall(endpoint, 'GET'), [apiCall]);
  const post = useCallback((endpoint, data) => apiCall(endpoint, 'POST', data), [apiCall]);
  const put = useCallback((endpoint, data) => apiCall(endpoint, 'PUT', data), [apiCall]);
  const del = useCallback((endpoint) => apiCall(endpoint, 'DELETE'), [apiCall]);

  return {
    apiCall,
    get,
    post,
    put,
    del,
    loading,
    error,
    setError
  };
};

export default useAdminApi;
